import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { useEmailTypoSuggestion } from '../hooks/useEmailTypoSuggestion';
import { apiCheckStackSocialCode } from '../utils/api';
import { LANGUAGE_STORAGE_KEY } from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';

const CHECK_DEBOUNCE_MS = 500;
const PLAN_LABEL_KEY = { lifetime_1tb: 'plan_1tb', lifetime_2tb: 'plan_2tb' };

function normalizeCode(raw) {
  return String(raw || '').trim().toLowerCase().replace(/[\s-]/g, '');
}

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3 8.5l3.2 3.2L13 5" stroke="#14804A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function StackSocialPage({ onNavigateHome, onTerms, onPrivacy, onGoToVault }) {
  const { t, i18n } = useTranslation('stacksocial');
  const { isAuthenticated, user, redeemStackSocialCode, redeemStackSocialCodeSelf } = useAuth();
  useLanguage(); // subscribes this component to language changes (re-renders on switch)

  // Three real screens, branched on auth state (2026-08-30 — see the fix
  // note in App.js for why this replaced hiding the whole page instead):
  //   - not authenticated: the original create-account + redeem form.
  //   - authenticated, free plan: same form, minus the email/password
  //     fields — the code applies straight to this account.
  //   - authenticated, already paid: no form at all, a message instead.
  // Never trust this client-side gate for the actual redemption itself —
  // routes/stacksocial.js's /redeem-self independently re-checks
  // hasPaidPlan(user) server-side before touching anything.
  const planId = user?.plan?.id || user?.plan || 'free';
  const alreadyPaid = isAuthenticated && planId !== 'free';
  const selfRedeemMode = isAuthenticated && !alreadyPaid;

  // StackSocial's audience is US-based — default this page to English
  // regardless of browser locale, but never override a language the
  // visitor already explicitly chose elsewhere on the site (see
  // i18n/index.js's detectInitialLanguage, which this deliberately
  // narrows rather than replaces).
  useEffect(() => {
    try {
      if (!localStorage.getItem(LANGUAGE_STORAGE_KEY)) i18n.changeLanguage('en');
    } catch {}
  }, [i18n]);

  // This page is deliberately unlinked from the rest of the site while
  // StackSocial tests the flow (see the project plan) — index.html's shared
  // <meta name="robots"> defaults to "index, follow" for every other route,
  // so this overrides it only while mounted and restores the default on
  // unmount, backing up robots.txt's own Disallow: /stacksocial.
  useEffect(() => {
    const tag = document.querySelector('meta[name="robots"]');
    const prev = tag ? tag.content : null;
    if (tag) tag.content = 'noindex, nofollow';
    return () => { if (tag && prev !== null) tag.content = prev; };
  }, []);

  const [codeInput, setCodeInput] = useState('');
  const [codeStatus, setCodeStatus] = useState('idle'); // idle | short | checking | valid | used | bad
  const [codePlan, setCodePlan] = useState(null);
  const [email, setEmail] = useState('');
  const emailTypo = useEmailTypoSuggestion(email);
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selfSuccess, setSelfSuccess] = useState(null); // { plan } once an authenticated self-redemption succeeds
  const debounceRef = useRef(null);

  const runCheck = useCallback(async (normalized) => {
    setCodeStatus('checking');
    try {
      const data = await apiCheckStackSocialCode(normalized);
      if (data.status === 'valid') { setCodeStatus('valid'); setCodePlan(data.plan); }
      else if (data.status === 'already_used') { setCodeStatus('used'); setCodePlan(null); }
      else { setCodeStatus('bad'); setCodePlan(null); } // not_found | invalid_format
    } catch {
      // Rate-limited or a real server error — don't claim the code is bad,
      // just fall back to "unknown" (idle) rather than showing a false
      // "we don't recognise this code" for what might be a perfectly valid one.
      setCodeStatus('idle'); setCodePlan(null);
    }
  }, []);

  useEffect(() => {
    const normalized = normalizeCode(codeInput);
    clearTimeout(debounceRef.current);
    setCodePlan(null);
    if (!normalized) { setCodeStatus('idle'); return; }
    // Anything other than exactly 10 (too short, or too long from a stray
    // paste/typo) shows the same local hint — no network call either way.
    if (normalized.length !== 10) { setCodeStatus('short'); return; }
    // Fires only once the field actually holds exactly 10 characters, after
    // a debounce — this plus the shared per-IP budget (see routes/
    // stacksocial.js) is what keeps this live-validation endpoint from
    // being a free way to enumerate codes.
    debounceRef.current = setTimeout(() => runCheck(normalized), CHECK_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [codeInput, runCheck]);

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError('');
    const normalized = normalizeCode(codeInput);
    if (normalized.length !== 10) return setFormError(t('code_short'));

    // Authenticated + free plan: applies straight to this account, no
    // email/password involved. Server independently re-checks
    // hasPaidPlan(user) before touching anything (routes/stacksocial.js's
    // /redeem-self) — already_paid below is the belt-and-braces case where
    // the user's plan changed in another tab between page load and submit.
    if (selfRedeemMode) {
      setSubmitting(true);
      try {
        const data = await redeemStackSocialCodeSelf(normalized);
        setSelfSuccess({ plan: data.plan });
        setSubmitting(false);
      } catch (err) {
        const map = {
          invalid_format: t('code_short'),
          not_found: t('code_bad'),
          already_used: t('err_already_used_self'),
          already_paid: t('already_paid_title'),
          rate_limited: t('err_rate_limited'),
        };
        setFormError(map[err.message] || t('err_generic'));
        setSubmitting(false);
      }
      return;
    }

    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return setFormError(t('err_email_invalid'));
    if (password.length < 10) return setFormError(t('err_password_short'));

    setSubmitting(true);
    try {
      await redeemStackSocialCode(email, password, normalized, {
        emailTypoSuggestionShown: emailTypo.everShown,
        emailTypoAccepted: emailTypo.accepted,
      });
      // Success sets user+encKey (isAuthenticated flips true) AND stamps a
      // real plan onto the new account — both would otherwise make this
      // same component re-render straight into the "already paid" screen
      // above instead of the vault. Navigate explicitly, right here, as a
      // direct result of this submit — never a silent fallthrough — which
      // also keeps the original timing for the recovery-token popup
      // (VaultPage reads the same zv_pending_recovery sessionStorage key
      // register() uses).
      onGoToVault && onGoToVault();
    } catch (err) {
      const map = {
        invalid_format: t('code_short'),
        not_found: t('code_bad'),
        already_used: t('code_used'),
        email_taken: t('err_email_taken'),
        password_too_short: t('err_password_short'),
        invalid_email: t('err_email_invalid'),
        rate_limited: t('err_rate_limited'),
        email_undeliverable: t('err_email_undeliverable'),
      };
      setFormError(map[err.message] || t('err_generic'));
      setSubmitting(false);
    }
  }

  const planLabel = codePlan ? t(PLAN_LABEL_KEY[codePlan]) : t('plan_placeholder');

  // Authenticated + already has a paid plan: no form, ever — the code is
  // never even looked up (see routes/stacksocial.js's /redeem-self, which
  // refuses before touching stacksocial_codes at all). An explicit message
  // instead of the vault, per the fix note in App.js.
  if (alreadyPaid) {
    const currentPlanLabel = t(PLAN_LABEL_KEY[planId] || 'plan_placeholder');
    return (
      <div className="ssr-root">
        <div className="ssr-top">
          <button className="ssr-logo" onClick={() => onNavigateHome && onNavigateHome()}>LifetimeCloud</button>
          <span className="ssr-sp" />
          <LanguageSwitcher />
        </div>
        <div className="ssr-wrap">
          <header className="ssr-head">
            <span className="ssr-eyebrow">{t('eyebrow')}</span>
            <h1 className="ssr-h1">{t('already_paid_title')}</h1>
            <p className="ssr-lede">
              <Trans t={t} i18nKey="already_paid_body" values={{ plan: currentPlanLabel }}
                components={{ 1: <a href="mailto:info@lifetimecloud.me" /> }} />
            </p>
          </header>
          <div className="ssr-cols">
            <div className="ssr-card">
              <button className="ssr-btn-primary" type="button" onClick={() => onGoToVault && onGoToVault()}>
                {t('go_to_vault')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated + free plan, just successfully redeemed: explicit
  // confirmation before the one deliberate exit into the vault — never a
  // silent landing there.
  if (selfSuccess) {
    const grantedPlanLabel = t(PLAN_LABEL_KEY[selfSuccess.plan] || 'plan_placeholder');
    return (
      <div className="ssr-root">
        <div className="ssr-top">
          <button className="ssr-logo" onClick={() => onNavigateHome && onNavigateHome()}>LifetimeCloud</button>
          <span className="ssr-sp" />
          <LanguageSwitcher />
        </div>
        <div className="ssr-wrap">
          <header className="ssr-head">
            <span className="ssr-eyebrow">{t('eyebrow')}</span>
            <h1 className="ssr-h1">{t('self_success_title')}</h1>
            <p className="ssr-lede">{t('self_success_body', { plan: grantedPlanLabel })}</p>
          </header>
          <div className="ssr-cols">
            <div className="ssr-card">
              <button className="ssr-btn-primary" type="button" onClick={() => onGoToVault && onGoToVault()}>
                {t('go_to_vault')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ssr-root">
      <div className="ssr-top">
        <button className="ssr-logo" onClick={() => onNavigateHome && onNavigateHome()}>LifetimeCloud</button>
        <span className="ssr-sp" />
        <LanguageSwitcher />
      </div>

      <div className="ssr-wrap">
        <header className="ssr-head">
          <span className="ssr-eyebrow">{t('eyebrow')}</span>
          <h1 className="ssr-h1">{t('h1')}</h1>
          <p className="ssr-lede">{selfRedeemMode ? t('logged_in_lede', { username: user?.username }) : t('lede')}</p>
        </header>

        <div className="ssr-cols">
          <form className="ssr-card" onSubmit={handleSubmit} noValidate>
            <div className="ssr-field">
              <label htmlFor="ssr-code">{t('l_code')}</label>
              <input
                id="ssr-code" className="ssr-input ssr-input-code ssr-mono"
                autoComplete="off" spellCheck="false" placeholder="xxxxxxxxxx" maxLength={14}
                value={codeInput} onChange={(e) => setCodeInput(e.target.value)}
                aria-invalid={codeStatus === 'used' || codeStatus === 'bad' ? 'true' : undefined}
              />
              <p className="ssr-hint">{t('h_code')}</p>
              {codeStatus === 'short' && <div className="ssr-codestate err">{t('code_short')}</div>}
              {codeStatus === 'used' && <div className="ssr-codestate err">{t('code_used')}</div>}
              {codeStatus === 'bad' && <div className="ssr-codestate err">{t('code_bad')}</div>}
              {codeStatus === 'valid' && <div className="ssr-codestate ok">{t('code_ok', { plan: t(PLAN_LABEL_KEY[codePlan]) })}</div>}
            </div>

            {!selfRedeemMode && (
              <>
                <div className="ssr-field">
                  <label htmlFor="ssr-email">{t('l_email')}</label>
                  <input id="ssr-email" className="ssr-input" type="email" autoComplete="email" placeholder="you@example.com"
                    value={email} onChange={(e) => setEmail(e.target.value)} />
                  {emailTypo.visible && (
                    <div className="ssr-email-suggestion">
                      <span>{t('email_suggestion_prefix')} <button type="button" className="ssr-email-suggestion-accept"
                        onClick={() => { setEmail(emailTypo.suggestion); emailTypo.markAccepted(); }}>{emailTypo.suggestion}</button>?</span>
                      <button type="button" className="ssr-email-suggestion-dismiss" onClick={emailTypo.dismiss} aria-label={t('email_suggestion_dismiss')}>✕</button>
                    </div>
                  )}
                </div>

                <div className="ssr-field">
                  <label htmlFor="ssr-pw">{t('l_pw')}</label>
                  <input id="ssr-pw" className="ssr-input" type="password" autoComplete="new-password" placeholder="••••••••••••"
                    value={password} onChange={(e) => setPassword(e.target.value)} />
                  <p className="ssr-hint">{t('h_pw')}</p>
                </div>

                <div className="ssr-warn">
                  <span className="ssr-warn-k">🔑</span>
                  <p><Trans t={t} i18nKey="warn" components={{ 1: <strong /> }} /></p>
                </div>
              </>
            )}

            {formError && <div className="ssr-form-err">{formError}</div>}

            <button className="ssr-btn-primary" type="submit" disabled={submitting}>
              {submitting
                ? t(selfRedeemMode ? 'cta_self_loading' : 'cta_loading')
                : t(selfRedeemMode ? 'cta_self' : 'cta')}
            </button>

            <p className="ssr-fine">
              <Trans t={t} i18nKey="fine" components={{
                1: <a onClick={(e) => { e.preventDefault(); onTerms && onTerms(); }} href="/terms" />,
                2: <a onClick={(e) => { e.preventDefault(); onPrivacy && onPrivacy(); }} href="/privacy" />,
              }} />
            </p>
          </form>

          <aside className="ssr-rail">
            <div className="ssr-card">
              <h2 className="ssr-rail-h2">{t('r_what')}</h2>
              <div className="ssr-plan-box">
                <span className="ssr-plan-size">{planLabel}</span>
                <span className="ssr-plan-for">{t('r_for')}</span>
              </div>
              <ul className="ssr-checks">
                <li><CheckIcon /><span><Trans t={t} i18nKey="r_check_1" components={{ 1: <b /> }} /></span></li>
                <li><CheckIcon /><span><Trans t={t} i18nKey="r_check_2" components={{ 1: <b /> }} /></span></li>
                <li><CheckIcon /><span><Trans t={t} i18nKey="r_check_3" components={{ 1: <b /> }} /></span></li>
                <li><CheckIcon /><span><Trans t={t} i18nKey="r_check_4" components={{ 1: <b /> }} /></span></li>
              </ul>
            </div>

            {/* "Already have an account? email us" no longer applies once
                logged in — this whole page IS that self-serve path now. */}
            {!selfRedeemMode && (
              <div className="ssr-support">
                <b>{t('s_title')}</b>
                <span><Trans t={t} i18nKey="s_body" components={{ 1: <a href="mailto:info@lifetimecloud.me" /> }} /></span>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
